import { ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { toast } from 'react-toastify';
// import ReactSwitch from 'react-switch';
import { ClipLoader } from 'react-spinners';
import { useAuth } from '../../context/auth.context';
import Button from '../../controls/Button';
import { AwsLoginErrors } from '../../constants/telosair-interfaces';
import authenticateService from '../../services/authenticateService';
import { RHFValidations, TOAST_MESSAGES } from '../../constants/global-constants';
import { getCurrentLoggedInCLient, getCurrentUser } from '../../services/dashboard-service';
import CustomInputBox from '../../controls/CustomInputBox';
import PasswordInputBox from '../../controls/PasswordInputBox';
import { GA_CATEGORIES, sendCustomEventToGa, setUserIdInGa } from '../../_helpers/ga';

type FORMDATA = {
  email: string;
  password: string;
};

/**
 * Login component for Account Activation.
 * @returns - Recat component for Login UI.
 */
export default function LoginComponent(): ReactElement {
  const { login, isLoggedIn } = useAuth();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  const methods = useForm<FORMDATA>({
    mode: 'all'
  });
  const { handleSubmit } = methods;

  /**
   * Function to check if user is onboarded.
   * @returns Is the user onboarded.
   */
  const checkIfNotOnBoarded = async (): Promise<boolean> => {
    try {
      const currentUser = await getCurrentUser();
      const clientResponse = await getCurrentLoggedInCLient(currentUser.data?.clientId);
      if (clientResponse.data.status === 'pending') {
        return true;
      }
      return false;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      return false;
    }
  };
  const navigate = useNavigate();
  const platform = Capacitor.getPlatform();
  /**
   * Handles the login action when the form is submitted.
   * @param  data - The form submit event.
   */
  const loginUser = async (data: FORMDATA): Promise<void> => {
    const { email: username, password } = data;
    if (login !== null) {
      setLoading(true);
      const { error, data: loginResponseData } = await login(username, password);

      if (!error && (await checkIfNotOnBoarded())) {
        setUserIdInGa(username);
        isLoggedIn();
        setLoading(false);
        navigate('/account-activation/device-setup');
        sendCustomEventToGa(GA_CATEGORIES.onboarding, 'initiated');
        return;
      }
      if (!error) {
        setUserIdInGa(username);
        isLoggedIn();
        setLoading(false);
        navigate('/');
        return;
      }
      if (error === AwsLoginErrors.newUser) {
        navigate(
          `/auth/set-temporary-password?email=${encodeURIComponent(
            username
          )}&token=${encodeURIComponent(loginResponseData?.token)}`
        );
        setLoading(false);
        return;
      }
      if (error === AwsLoginErrors.unconfirmed) {
        toast.error('Please verify your account before logging in.');
        authenticateService.resendConfirmationCode(username, (resendFailed: boolean) =>
          resendFailed
            ? toast.error(TOAST_MESSAGES.error)
            : navigate(`/auth/verify-otp?email=${encodeURIComponent(username)}`)
        );
        setLoading(false);
        return;
      }
      if (error === AwsLoginErrors.invalidCredentials) {
        toast.error('Invalid email or password');
        setLoading(false);
        return;
      }
      if (error === AwsLoginErrors.notFound) {
        toast.error('User not found');
        setLoading(false);
        return;
      }
      setLoading(false);
      toast.error(TOAST_MESSAGES.error);
    }
  };

  useEffect(() => {
    const session = params.get('session');
    if (session && session === 'expired') {
      // toast.error('Session expired. Please login again.');
      setParams(undefined);
    }
  }, [params]);

  return (
    <div className="flex flex-col gap-8 w-full">
      <div className="text-3xl font-bold dark:text-white text-black text-center">
        Welcome to TelosAir
      </div>
      {/* <div className="text-gray-400 mt-1">Start managing your devices for free</div> */}
      <div className="dark:bg-container-700 bg-white p-6 rounded w-full">
        <div className="flex justify-between items-center">
          <div className="text-xl dark:text-white text-black font-semibold">Sign In</div>
        </div>
        <div className="text-gray-400 mt-1">Please enter your registered credentials to login</div>
        {/* eslint-disable-next-line */}
        <FormProvider {...methods}>
          <form
            noValidate
            className="mt-8 2xl:mt-12"
            action="post"
            onSubmit={handleSubmit(loginUser)}
          >
            <CustomInputBox
              label="Email"
              type="email"
              autoComplete="email"
              idname="email"
              validationObject={RHFValidations.emailRequired}
            />
            <PasswordInputBox
              label="Password"
              idname="password"
              autoComplete="current-password"
              validationObject={{ required: 'Password is required' }}
            />
            {/* <div className="flex items-center justify-between mt-8">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <ReactSwitch
                  width={36}
                  height={20}
                  onChange={(checked) => setRemeberMe(checked)}
                  checked={remberMe}
                  uncheckedIcon={false}
                  onColor="#2970FF"
                  checkedIcon={false}
                  handleDiameter={16}
                />
              </div>
              <div className="ml-3 text-sm">
                <h1 className="text-gray-500 text-sm font-medium">Remember me</h1>
              </div>
            </div>
          </div> */}
            <button
              type="submit"
              className="w-full relative mt-12 text-white bg-primary-600 hover:bg-primary-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg flex justify-center items-center text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
            >
              {loading ? <ClipLoader size={20} color="#fff" /> : 'Sign In'}
            </button>
            <div className="w-full justify-center items-center flex mt-5">
              <button
                onClick={() => navigate('/auth/forgot-password')}
                type="button"
                className="font-medium text-primary-500 text-sm underline text-center"
              >
                Forgot Password?
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
      <div className="flex justify-center items-center flex-row gap-3">
        <div className="font-medium text-gray-500">New to TelosAir?</div>
        <div className="w-40">
          <Button
            onClick={() => navigate('/auth/user-details')}
            isBlockButton
            size="sm"
            theme="secondary-gray"
            name="Sign Up"
          />
        </div>
        {platform !== 'web' && (
          <Button
            onClick={() => navigate('/connect-devices')}
            isBlockButton
            size="sm"
            theme="secondary-gray"
            name="Connect devices"
          />
        )}
      </div>
    </div>
  );
}
